/* TOMB CLOCK — product page: variant resolution via Section Rendering API, cart form,
   gallery + zoom, fit guide modal, sticky purchase bar. */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
const S = () => (window.TC && window.TC.strings) || {};
const mqMobile = window.matchMedia('(max-width: 760px)');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function trapFocus(container, event) {
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[hidden]'));
  if (!items.length) {
    event.preventDefault();
    container.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  if (!container.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

class TcProduct extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.url = this.dataset.url;
    this.form = this.querySelector('form[data-tc-product-form]');
    this.track = this.querySelector('[data-tc-track]');
    this.sticky = this.querySelector('[data-tc-sticky]');
    this.modal = null;
    this.modalTrigger = null;

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onKey = this.onKey.bind(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('keydown', this.onKey);

    this.setupDots();
    this.setupSticky();
    this.setupSwipe();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
    this.dotObserver?.disconnect();
    this.stickyObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.lockObserver?.disconnect();
    this.abort?.abort();
    if (this.modal) this.unlock();
  }

  /* ---------- Variant selection ---------- */
  selectedOptionValueIds() {
    const picker = this.querySelector('[data-tc-picker]');
    if (!picker) return [];
    const ids = [];
    picker.querySelectorAll('fieldset').forEach((fs) => {
      const radio = fs.querySelector('[data-tc-option-radio]:checked');
      const select = fs.querySelector('[data-tc-option-select]');
      if (radio) ids.push(radio.dataset.optionValueId);
      else if (select) ids.push(select.value);
    });
    return ids;
  }

  onChange(event) {
    const target = event.target;
    if (target.matches('[data-tc-option-radio], [data-tc-option-select]')) {
      this.renderVariant(target);
    }
  }

  async renderVariant(source) {
    const ids = this.selectedOptionValueIds();
    const focusId = source?.dataset.optionValueId || source?.value;
    const focusIsSelect = source?.matches('select');
    this.abort?.abort();
    const ctrl = new AbortController();
    this.abort = ctrl;
    this.querySelector('[data-tc-picker]')?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${this.url}?option_values=${ids.join(',')}&section_id=${this.sectionId}`, { signal: ctrl.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const html = await res.text();
      if (ctrl.signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('tc-product');
      if (!next) throw new Error('missing section');
      if (next.dataset.productId !== this.dataset.productId) {
        window.location.href = next.dataset.url;
        return;
      }
      const qtyInput = this.querySelector('[data-tc-qty-main]');
      const keepQty = qtyInput ? Number(qtyInput.value) : null;
      this.querySelectorAll('[data-tc-sync]').forEach((el) => {
        const replacement = next.querySelector(`[data-tc-sync="${CSS.escape(el.dataset.tcSync)}"]`);
        if (replacement) el.innerHTML = replacement.innerHTML;
      });
      const variantId = next.dataset.variantId;
      this.dataset.variantId = variantId;
      const input = this.querySelector('[data-tc-variant-input]');
      if (input) {
        input.value = variantId;
        input.disabled = !variantId;
      }
      const newQty = this.querySelector('[data-tc-qty-main]');
      if (newQty && keepQty && keepQty >= Number(newQty.min || 1) && (!newQty.max || keepQty <= Number(newQty.max))) newQty.value = keepQty;
      const sticky = this.querySelector('[data-tc-atc-sticky]');
      const nextSticky = next.querySelector('[data-tc-atc-sticky]');
      if (sticky && nextSticky) {
        sticky.disabled = nextSticky.disabled;
        sticky.textContent = nextSticky.textContent;
      }
      if (variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }
      this.showMedia(next.dataset.featuredMedia);
      this.clearMessages();
      if (focusId) {
        const el = focusIsSelect
          ? this.querySelector(`[data-tc-option-select] option[value="${CSS.escape(focusId)}"]`)?.closest('select')
          : this.querySelector(`[data-tc-option-radio][data-option-value-id="${CSS.escape(focusId)}"]`);
        el?.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('tc:variant-change', { bubbles: true, detail: { variantId } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.showError((S().network_error) || 'Something went wrong. Please refresh the page.');
    } finally {
      if (!ctrl.signal.aborted) this.querySelector('[data-tc-picker]')?.removeAttribute('aria-busy');
    }
  }

  showMedia(mediaId) {
    if (!mediaId || !this.track) return;
    const cell = this.track.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!cell) return;
    if (mqMobile.matches || this.track.hasAttribute('data-tc-slider')) {
      this.track.scrollTo({ left: cell.offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    } else if (this.track.firstElementChild !== cell) {
      this.track.prepend(cell);
    }
  }

  /* ---------- Add to cart ---------- */
  setBusy(busy, isAdd = true) {
    const main = this.querySelector('[data-tc-atc]');
    const sticky = this.querySelector('[data-tc-atc-sticky]');
    [main, sticky].forEach((btn) => {
      if (!btn) return;
      if (busy) {
        btn.dataset.wasDisabled = btn.disabled ? '1' : '';
        btn.disabled = true;
        if (isAdd) btn.setAttribute('aria-busy', 'true');
      } else {
        btn.disabled = btn.dataset.wasDisabled === '1';
        btn.removeAttribute('aria-busy');
      }
    });
    if (isAdd && main) {
      const label = main.querySelector('[data-tc-atc-label]');
      if (busy && label) {
        this.prevLabel = label.textContent;
        label.textContent = S().adding || 'Adding…';
      } else if (!busy && label && this.prevLabel) {
        label.textContent = this.prevLabel;
      }
    }
  }

  clearMessages() {
    const err = this.querySelector('[data-tc-atc-error]');
    if (err) {
      err.hidden = true;
      err.textContent = '';
    }
  }

  showError(message) {
    const err = this.querySelector('[data-tc-atc-error]');
    if (err) {
      err.textContent = message;
      err.hidden = false;
    }
  }

  async onSubmit(event) {
    const form = event.target;
    if (form !== this.form) return;
    if (!window.TC?.cart?.add) return; /* native POST fallback */
    event.preventDefault();
    const main = this.querySelector('[data-tc-atc]');
    if (main?.getAttribute('aria-busy') === 'true') return;
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    if (!id) {
      this.showError(S().unavailable || 'Unavailable');
      return;
    }
    const item = { id, quantity: Math.max(1, parseInt(fd.get('quantity'), 10) || 1) };
    const plan = fd.get('selling_plan');
    if (plan) item.selling_plan = Number(plan);
    const properties = {};
    for (const [key, value] of fd.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== '' && !(value instanceof File && !value.size)) properties[m[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    this.clearMessages();
    this.setBusy(true);
    try {
      await window.TC.cart.add([item], { target: form });
      this.setBusy(false);
      const label = main?.querySelector('[data-tc-atc-label]');
      const status = this.querySelector('[data-tc-atc-status]');
      if (main) main.dataset.state = 'added';
      if (label) label.textContent = `${S().added || 'Added'} ✓`;
      if (status) status.textContent = S().added || 'Added to cart';
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        if (main) delete main.dataset.state;
        if (label && this.prevLabel) label.textContent = this.prevLabel;
        if (status) status.textContent = '';
      }, 1400);
      const title = this.querySelector('.tc-pdp__title')?.textContent?.trim();
      window.TC.afterAdd?.(title);
    } catch (error) {
      this.setBusy(false);
      this.showError(error.message);
    }
  }

  /* ---------- Clicks: quantity, gallery, modals ---------- */
  onClick(event) {
    const step = event.target.closest('[data-tc-step]');
    if (step) {
      const input = this.querySelector('[data-tc-qty-main]');
      if (!input) return;
      const inc = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const next = (Number(input.value) || min) + Number(step.dataset.tcStep) * inc;
      input.value = Math.min(max, Math.max(min, next));
      return;
    }
    const gstep = event.target.closest('[data-tc-gallery-step]');
    if (gstep && this.track) {
      const cells = [...this.track.children];
      const width = this.track.clientWidth || 1;
      const current = Math.round(this.track.scrollLeft / width);
      const nextIndex = (Math.abs(current) + Number(gstep.dataset.tcGalleryStep) + cells.length) % cells.length;
      const cell = cells[nextIndex];
      if (cell) this.track.scrollTo({ left: cell.offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      return;
    }
    const dot = event.target.closest('[data-tc-dot]');
    if (dot && this.track) {
      const cell = this.track.children[Number(dot.dataset.tcDot)];
      if (cell) this.track.scrollTo({ left: cell.offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      return;
    }
    const zoom = event.target.closest('[data-tc-zoom]');
    if (zoom) {
      this.openZoom(zoom.dataset.tcZoom, zoom);
      return;
    }
    const zstep = event.target.closest('[data-tc-zoom-step]');
    if (zstep) {
      this.stepZoom(Number(zstep.dataset.tcZoomStep));
      return;
    }
    const opener = event.target.closest('[data-tc-open-modal]');
    if (opener) {
      this.openModal(this.querySelector(`[data-tc-modal="${CSS.escape(opener.dataset.tcOpenModal)}"]`), opener);
      return;
    }
    if (event.target.closest('[data-tc-modal-close]')) this.closeModal();
  }

  lock() {
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.overflow = 'hidden';
  }

  unlock() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.overflow = '';
  }

  openModal(modal, trigger) {
    if (!modal) return;
    if (this.modal) this.closeModal(false);
    this.modal = modal;
    this.modalTrigger = trigger || document.activeElement;
    modal.hidden = false;
    this.lock();
    requestAnimationFrame(() => (modal.querySelector('[role="dialog"]') || modal).focus({ preventScroll: true }));
  }

  closeModal(restore = true) {
    const modal = this.modal;
    if (!modal) return;
    modal.querySelectorAll('video').forEach((v) => v.pause());
    modal.querySelectorAll('iframe').forEach((f) => f.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*'));
    modal.hidden = true;
    this.modal = null;
    this.unlock();
    if (restore && this.modalTrigger && document.contains(this.modalTrigger)) this.modalTrigger.focus({ preventScroll: true });
  }

  onKey(event) {
    if (!this.modal) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
    } else if (event.key === 'Tab') {
      trapFocus(this.modal.querySelector('[role="dialog"]') || this.modal, event);
    } else if (this.modal.dataset.tcModal === 'zoom' && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      const rtl = document.dir === 'rtl' ? -1 : 1;
      this.stepZoom((event.key === 'ArrowRight' ? 1 : -1) * rtl);
    }
  }

  zoomItems() {
    return [...this.querySelectorAll('[data-tc-zoom-stage] .tc-zoom__item')];
  }

  openZoom(mediaId, trigger) {
    const modal = this.querySelector('[data-tc-modal="zoom"]');
    this.zoomOrder = [...this.querySelectorAll('[data-tc-track] [data-media-id]')].map((c) => c.dataset.mediaId);
    this.showZoom(mediaId);
    this.openModal(modal, trigger);
  }

  showZoom(mediaId) {
    this.zoomId = mediaId;
    this.zoomItems().forEach((item) => {
      const active = item.dataset.mediaId === String(mediaId);
      if (!active) item.querySelectorAll('video').forEach((v) => v.pause());
      item.hidden = !active;
      if (active) item.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    });
    const status = this.querySelector('[data-tc-zoom-status]');
    const order = this.zoomOrder || [];
    if (status && order.length) status.textContent = `${order.indexOf(String(mediaId)) + 1} / ${order.length}`;
  }

  stepZoom(dir) {
    const order = this.zoomOrder || [];
    if (order.length < 2) return;
    const i = order.indexOf(String(this.zoomId));
    this.showZoom(order[(i + dir + order.length) % order.length]);
  }

  setupSwipe() {
    const panel = this.querySelector('.tc-zoom__panel');
    if (!panel) return;
    let x0 = null;
    let y0 = null;
    panel.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      x0 = e.clientX;
      y0 = e.clientY;
    });
    panel.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.stepZoom(dx < 0 ? 1 : -1);
    });
    panel.addEventListener('pointercancel', () => (x0 = null));
  }

  /* ---------- Mobile dots ---------- */
  setupDots() {
    const dots = [...this.querySelectorAll('[data-tc-dot]')];
    if (!this.track || !dots.length || !('IntersectionObserver' in window)) return;
    this.dotObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          const index = [...this.track.children].indexOf(entry.target);
          dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
        });
      },
      { root: this.track, threshold: [0.6] }
    );
    [...this.track.children].forEach((c) => this.dotObserver.observe(c));
  }

  /* ---------- Sticky bar ---------- */
  setupSticky() {
    const main = this.querySelector('[data-tc-atc]');
    if (!this.sticky || !main || !('IntersectionObserver' in window)) return;
    this.state = { pastButton: false, footer: false };
    const apply = () => {
      const locked = document.documentElement.hasAttribute('scroll-lock');
      const show = this.state.pastButton && !this.state.footer && !locked;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', String(!show));
      this.sticky.inert = !show;
    };
    this.stickyObserver = new IntersectionObserver((entries) => {
      const e = entries[0];
      this.state.pastButton = !e.isIntersecting && e.boundingClientRect.top < 0;
      apply();
    });
    this.stickyObserver.observe(main);
    const footer = document.querySelector('.tc-footer') || document.querySelector('footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver((entries) => {
        this.state.footer = entries[0].isIntersecting;
        apply();
      });
      this.footerObserver.observe(footer);
    }
    this.lockObserver = new MutationObserver(apply);
    this.lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });
  }
}

if (!customElements.get('tc-product')) customElements.define('tc-product', TcProduct);
